import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, TrendingDown, TrendingUp, Vault, Wallet } from 'lucide-react';
import clsx from 'clsx';

import Avatar from '@/components/Avatar';
import MonthBars from '@/components/MonthBars';
import PeriodPicker from '@/components/PeriodPicker';
import { apiErrorMessage, apiGet } from '@/lib/api';
import type { BackState } from '@/lib/back-nav';
import { formatDate, formatKop } from '@/lib/format';
import { deltaPercent, monthLong, periodLabel, recallPeriod, rememberPeriod, type PeriodValue } from '@/lib/period';
import { METHOD_BAR, METHOD_LABEL, ROLE_LABEL, SALES_METHODS, type DirectorOverview, type RatingRow } from '@/lib/sales';
import { pluralEstablishments } from '@/lib/subscription';
import { useAuthStore } from '@/store/auth.store';

function query(p: PeriodValue): string {
  return `from=${p.from}&to=${p.to}`;
}

export default function DirectorDashboard() {
  const [period, setPeriodState] = useState<PeriodValue>(recallPeriod);
  const [overview, setOverview] = useState<DirectorOverview | null>(null);
  const [rating, setRating] = useState<RatingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [month, setMonth] = useState<string | null>(null);

  const setPeriod = (v: PeriodValue) => {
    rememberPeriod(v);
    setPeriodState(v);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const q = query(period);
    void Promise.all([
      apiGet<DirectorOverview>(`/director/overview?${q}`, { silent: true }),
      apiGet<{ items: RatingRow[] }>(`/director/rating?${q}`, { silent: true }),
    ])
      .then(([o, r]) => {
        if (cancelled) return;
        setOverview(o);
        setRating(r.items);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, 'Не удалось загрузить сводку'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period]);

  if (!overview) {
    return (
      <div className="max-w-5xl space-y-5">
        <Header period={period} onPeriod={setPeriod} overview={null} />
        {error ? (
          <div className="card p-4 text-sm text-ink/70">{error}</div>
        ) : (
          <div className="text-sm text-ink/50">Загрузка…</div>
        )}
      </div>
    );
  }

  const o = overview;
  const selected = month ?? o.series[o.series.length - 1]?.month ?? null;
  const selectedRow = o.series.find((s) => s.month === selected) ?? null;

  return (
    <div className={clsx('max-w-5xl space-y-5 transition-opacity', loading && 'opacity-60')}>
      <Header period={period} onPeriod={setPeriod} overview={o} />
      {error ? <div className="card p-4 text-sm text-rose-700">{error}</div> : null}

      <section className="card grid divide-y divide-line/70 md:grid-cols-3 md:divide-x md:divide-y-0">
        <div className="p-5">
          <h2 className="text-sm text-ink/55">Оборот компании</h2>
          <div className="mt-1 text-3xl font-semibold tracking-tight tabular-nums">{formatKop(o.turnover.kopecks)}</div>
          <Delta current={o.turnover.kopecks} previous={o.turnover.previousKopecks} />
          <p className="mt-3 text-xs text-ink/50">
            {o.turnover.payments} оплат · {o.turnover.payers} {pluralEstablishments(o.turnover.payers)}
            {o.turnover.payments > 0 ? ` · средний чек ${formatKop(o.turnover.averageKopecks)}` : ''}
          </p>
        </div>
        <div className="p-5">
          <h2 className="text-sm text-ink/55">В сейф · {o.safe.percent} %</h2>
          <div className="mt-1 text-3xl font-semibold tracking-tight tabular-nums">{formatKop(o.safe.inPeriodKopecks)}</div>
          <p className="mt-3 text-xs leading-relaxed text-ink/50">
            Доля каждой оплаты подписки за период. Процент задаёт администратор Resstro.
          </p>
        </div>
        <div className="p-5">
          <h2 className="flex items-center gap-1.5 text-sm text-ink/55">
            <Vault size={15} aria-hidden /> Остаток сейфа
          </h2>
          <div
            className={clsx(
              'mt-1 text-3xl font-semibold tracking-tight tabular-nums',
              o.safe.balanceKopecks < 0 && 'text-rose-600',
            )}
          >
            {formatKop(o.safe.balanceKopecks)}
          </div>
          <dl className="mt-3 space-y-1 text-xs">
            <LedgerRow label={`Поступило с ${formatDate(o.safe.startDate)}`} kopecks={o.safe.inflowKopecks} />
            <LedgerRow label="Расходы из сейфа" kopecks={-o.safe.expensesKopecks} />
            <LedgerRow label="Выплаты менеджерам" kopecks={-o.safe.payoutsKopecks} />
          </dl>
        </div>
      </section>

      <section className="card p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-medium">Оборот по месяцам</h2>
          <div className="flex items-center gap-3 text-xs text-ink/55">
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden className="h-2.5 w-2.5 rounded-sm bg-orange" /> оборот
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden className="h-2.5 w-2.5 rounded-sm bg-ink/80" /> в сейф
            </span>
          </div>
        </div>
        <div className="mt-4">
          <MonthBars
            label="Оборот по месяцам"
            data={o.series.map((s) => ({ month: s.month, value: s.turnoverKopecks, inner: s.safeInKopecks }))}
            selected={selected}
            onSelect={setMonth}
          />
        </div>
        {selectedRow ? (
          <div className="mt-4 border-t border-line pt-4">
            <div className="text-sm font-medium">{monthLong(selectedRow.month)}</div>
            <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
              <MonthStat label="Оборот" kopecks={selectedRow.turnoverKopecks} />
              <MonthStat label="В сейф" kopecks={selectedRow.safeInKopecks} />
              <MonthStat label="Расходы" kopecks={selectedRow.expensesKopecks} />
              <MonthStat label="Выплаты" kopecks={selectedRow.payoutsKopecks} />
            </dl>
          </div>
        ) : null}
      </section>

      <div className="grid gap-5 lg:grid-cols-5">
        <EstablishmentsCard data={o.establishments} />
        <ExpensesCard overview={o} />
      </div>

      <RatingCard rows={rating} />

      <Link to="/me" className="btn-ghost w-full md:hidden">
        <Wallet size={16} /> Личный кабинет: ставка, начисления, выплаты
      </Link>
    </div>
  );
}

function Header({
  period,
  onPeriod,
  overview,
}: {
  period: PeriodValue;
  onPeriod: (v: PeriodValue) => void;
  overview: DirectorOverview | null;
}) {
  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold sm:text-2xl">Отдел продаж</h1>
        <p className="mt-1 text-sm text-ink/55">
          {periodLabel(period.from, period.to)}
          {overview
            ? ` · сравнение с ${periodLabel(overview.period.previousFrom, overview.period.previousTo)}`
            : ''}
        </p>
      </div>
      <PeriodPicker value={period} onChange={onPeriod} />
    </div>
  );
}

function Delta({ current, previous }: { current: number; previous: number }) {
  const d = deltaPercent(current, previous);
  if (d === null) {
    return (
      <div className="mt-2 text-xs text-ink/45">
        {current > 0 ? 'В прошлом периоде оплат не было' : 'Оплат пока нет'}
      </div>
    );
  }
  const up = d >= 0;
  return (
    <div className={clsx('mt-2 inline-flex items-center gap-1 text-xs font-medium', up ? 'text-emerald-700' : 'text-rose-600')}>
      {up ? <TrendingUp size={14} aria-hidden /> : <TrendingDown size={14} aria-hidden />}
      {up ? '+' : ''}
      {d} % к прошлому периоду
    </div>
  );
}

function LedgerRow({ label, kopecks }: { label: string; kopecks: number }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink/50">{label}</dt>
      <dd className={clsx('font-medium tabular-nums', kopecks < 0 ? 'text-ink/70' : 'text-emerald-700')}>
        {kopecks > 0 ? '+' : kopecks < 0 ? '−' : ''}
        {formatKop(Math.abs(kopecks))}
      </dd>
    </div>
  );
}

function MonthStat({ label, kopecks }: { label: string; kopecks: number }) {
  return (
    <div>
      <dt className="text-xs text-ink/50">{label}</dt>
      <dd className="font-medium tabular-nums">{formatKop(kopecks)}</dd>
    </div>
  );
}

const SEGMENTS = [
  { key: 'paying', label: 'Платят', dot: 'bg-emerald-500' },
  { key: 'trial', label: 'Пробный период', dot: 'bg-sky-400' },
  { key: 'grace', label: 'Просрочили', dot: 'bg-amber-400' },
  { key: 'blocked', label: 'Доступ закрыт', dot: 'bg-rose-500' },
  { key: 'noSubscription', label: 'Без подписки', dot: 'bg-ink/20' },
] as const;

function EstablishmentsCard({ data }: { data: DirectorOverview['establishments'] }) {
  const values: Record<(typeof SEGMENTS)[number]['key'], number> = {
    paying: data.paying,
    trial: data.trial,
    grace: data.grace,
    blocked: data.blocked,
    noSubscription: data.noSubscription ?? 0,
  };
  const sum = SEGMENTS.reduce((s, seg) => s + values[seg.key], 0);
  return (
    <section className="card p-5 lg:col-span-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-medium">Заведения</h2>
        <Link to="/establishments" className="inline-flex items-center text-sm text-orange hover:underline">
          Все <ChevronRight size={15} aria-hidden />
        </Link>
      </div>
      <p className="mt-1 text-sm text-ink/55">
        {data.total} на платформе · {data.attachedToManagers} закреплены за менеджерами
      </p>
      <div className="mt-4 flex h-2.5 divide-x divide-white overflow-hidden rounded-full bg-line">
        {sum > 0
          ? SEGMENTS.filter((seg) => values[seg.key] > 0).map((seg) => (
              <div key={seg.key} className={seg.dot} style={{ width: `${(values[seg.key] / sum) * 100}%` }} />
            ))
          : null}
      </div>
      <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-3">
        {SEGMENTS.filter((seg) => seg.key !== 'noSubscription' || values.noSubscription > 0).map((seg) => (
          <li key={seg.key} className="flex items-center gap-2">
            <span aria-hidden className={clsx('h-2 w-2 shrink-0 rounded-full', seg.dot)} />
            <span className="text-ink/60">{seg.label}</span>
            <span className="ml-auto font-medium tabular-nums">{values[seg.key]}</span>
          </li>
        ))}
      </ul>
      <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-line pt-4">
        <div>
          <dt className="text-xs text-ink/50">Новых за период</dt>
          <dd className="mt-0.5 text-xl font-semibold tabular-nums">{data.newInPeriod}</dd>
        </div>
        <div>
          <dt className="text-xs text-ink/50">Впервые оплатили</dt>
          <dd className="mt-0.5 text-xl font-semibold tabular-nums">{data.firstPaidInPeriod}</dd>
        </div>
      </dl>
    </section>
  );
}

function ExpensesCard({ overview }: { overview: DirectorOverview }) {
  const { expenses, payouts } = overview;
  return (
    <section className="card p-5 lg:col-span-2">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-medium">Расходы отдела</h2>
        <Link to="/expenses" className="inline-flex items-center text-sm text-orange hover:underline">
          Все <ChevronRight size={15} aria-hidden />
        </Link>
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{formatKop(expenses.totalKopecks)}</div>
      <ul className="mt-3 space-y-2.5">
        {SALES_METHODS.map((m) => {
          const v = expenses.byMethod[m];
          const share = expenses.totalKopecks > 0 ? (v / expenses.totalKopecks) * 100 : 0;
          return (
            <li key={m}>
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-ink/60">{METHOD_LABEL[m]}</span>
                <span className="font-medium tabular-nums">{formatKop(v)}</span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-line/70">
                <div className={clsx('h-full rounded-full', METHOD_BAR[m])} style={{ width: `${share}%` }} />
              </div>
            </li>
          );
        })}
      </ul>
      <p className="mt-4 border-t border-line pt-3 text-xs text-ink/50">
        Выплаты менеджерам за период: <span className="font-medium text-ink/70">{formatKop(payouts.kopecks)}</span>
        {payouts.count ? ` · ${payouts.count}` : ''}
      </p>
    </section>
  );
}

/** Призовые места подсвечиваем только тем, у кого был оборот. */
function RankBadge({ row }: { row: RatingRow }) {
  const podium = row.rank <= 3 && row.turnoverKopecks > 0;
  return (
    <span
      className={clsx(
        'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums',
        podium ? 'bg-orange text-white' : 'bg-paper text-ink/55 ring-1 ring-line',
      )}
    >
      {row.rank}
    </span>
  );
}

function RoleTag({ role }: { role: RatingRow['role'] }) {
  if (role === 'MANAGER') return null;
  return (
    <span className="rounded-md bg-orange/10 px-1.5 py-0.5 text-[11px] font-medium text-orange-700">{ROLE_LABEL[role]}</span>
  );
}

function DeletedTag({ row }: { row: RatingRow }) {
  if (!row.deleted) return null;
  return <span className="rounded-md bg-ink/5 px-1.5 py-0.5 text-[11px] font-medium text-ink/55">удалён</span>;
}

const RATING_BACK: BackState = { backTo: '/', backSteps: 1, backLabel: 'Обзор' };

/** Своя строка — в личный кабинет, удалённого открыть нельзя. */
function memberHref(row: RatingRow, myId: string | undefined): string | null {
  if (row.deleted) return null;
  return row.id === myId ? '/me' : `/managers/${row.id}`;
}

function RatingCard({ rows }: { rows: RatingRow[] }) {
  const myId = useAuthStore((s) => s.manager?.id);
  return (
    <section className="card overflow-hidden">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-5 py-4">
        <h2 className="font-medium">Рейтинг менеджеров</h2>
        <span className="text-xs text-ink/50">по обороту закреплённых заведений за период</span>
      </div>
      {rows.length === 0 ? (
        <div className="px-5 py-6 text-sm text-ink/55">В команде пока нет менеджеров.</div>
      ) : (
        <>
          <table className="hidden w-full text-sm sm:table">
            <thead>
              <tr className="border-b border-line text-left text-xs text-ink/50">
                <th className="w-12 px-5 py-2 font-medium">#</th>
                <th className="px-2 py-2 font-medium">Менеджер</th>
                <th className="px-2 py-2 text-right font-medium">Оборот</th>
                <th className="px-2 py-2 text-right font-medium">Новые</th>
                <th className="px-2 py-2 text-right font-medium">Платят</th>
                <th className="px-5 py-2 text-right font-medium">Комиссия</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const href = memberHref(r, myId);
                return (
                <tr key={r.id} className={clsx('border-b border-line/60 last:border-0', r.status === 'blocked' && 'text-ink/45')}>
                  <td className="px-5 py-3">
                    <RankBadge row={r} />
                  </td>
                  <td className="px-2 py-3">
                    <div className="flex items-center gap-2.5">
                      <Avatar name={r.name} url={r.avatarUrl} size={32} />
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {href ? (
                            <Link to={href} state={RATING_BACK} className="font-medium hover:text-orange">
                              {r.name}
                            </Link>
                          ) : (
                            <span className="font-medium">{r.name}</span>
                          )}
                          <RoleTag role={r.role} />
                          <DeletedTag row={r} />
                        </div>
                        <div className="text-xs text-ink/45">
                          {r.establishments} {pluralEstablishments(r.establishments)} · {r.code}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-3 text-right font-semibold tabular-nums">{formatKop(r.turnoverKopecks)}</td>
                  <td className="px-2 py-3 text-right tabular-nums">{r.newEstablishments}</td>
                  <td className="px-2 py-3 text-right tabular-nums">{r.payingEstablishments}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-ink/70">{formatKop(r.commissionKopecks)}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
          <ul className="divide-y divide-line/60 sm:hidden">
            {rows.map((r) => {
              const href = memberHref(r, myId);
              const body = (
                <>
                  <RankBadge row={r} />
                  <Avatar name={r.name} url={r.avatarUrl} size={32} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="truncate font-medium">{r.name}</span>
                      <RoleTag role={r.role} />
                      <DeletedTag row={r} />
                    </div>
                    <div className="mt-0.5 text-xs text-ink/50">
                      новых {r.newEstablishments} · платят {r.payingEstablishments} · комиссия {formatKop(r.commissionKopecks)}
                    </div>
                  </div>
                  <div className="shrink-0 text-right font-semibold tabular-nums">{formatKop(r.turnoverKopecks)}</div>
                  {href ? <ChevronRight size={16} className="shrink-0 text-ink/30" /> : null}
                </>
              );
              return (
                <li key={r.id} className={clsx(r.status === 'blocked' && 'text-ink/45')}>
                  {href ? (
                    <Link to={href} state={RATING_BACK} className="flex items-center gap-3 px-4 py-3 active:bg-paper">
                      {body}
                    </Link>
                  ) : (
                    <div className="flex items-center gap-3 px-4 py-3">{body}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
